import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Formation } from '@/types/Concept.types'
import type { Play } from '@/hooks/UsePlaybookData'
import type { ConceptType } from '../Types'

type ModalName = 'settings' | 'share' | 'rename' | 'export' | 'playViewer'

interface UsePlaybookActionsParams {
	playbookId: string | undefined
	plays: Play[]

	// Data hooks
	playData: {
		createPlay: (name: string) => Promise<Play>
		updatePlay: (playId: string, updates: Partial<Play>) => Promise<void>
		deletePlay: (playId: string) => Promise<void>
		duplicatePlay: (playId: string) => Promise<Play>
		refetch: () => Promise<void>
	}

	// State actions
	stateActions: {
		resetNewItemState: () => void
		resetRenameState: () => void
		setRenamePlayId: (id: string | null) => void
		setRenamePlayName: (name: string) => void
		setViewingPlayId: (id: string | null) => void
	}

	// Modal actions
	modals: {
		open: (name: ModalName) => void
		close: (name: ModalName) => void
	}

	// Confirmation dialogs
	deletePlayConfirm: { prompt: (playId: string) => void }
	deleteConceptConfirm: { prompt: (item: { id: string; type: ConceptType }) => void }

	// Callbacks
	onOpenPlay?: (playId: string) => void

	// Current state
	renamePlayId: string | null
	renamePlayName: string
}

interface UsePlaybookActionsReturn {
	// Derived data
	allPlays: Play[]

	// Play actions
	handleNewPlay: () => Promise<void>
	handleOpenPlay: (playId: string) => void
	handleAnimatePlay: (playId: string) => void
	handleRenamePlay: (playId: string) => void
	confirmRename: () => Promise<void>
	handleDeletePlay: (playId: string) => void
	handleDuplicatePlay: (playId: string) => Promise<void>

	// Concept/Formation actions
	handleDeleteConcept: (id: string, type: ConceptType) => void
	handleNewFormation: () => void
	handleEditFormation: (formation: Formation) => void
}

/**
 * Hook for PlaybookEditor action handlers
 *
 * Consolidates all mutation and action handlers into a single hook
 * to reduce complexity in the main component.
 */
export function usePlaybookActions({
	playbookId,
	plays,
	playData,
	stateActions,
	modals,
	deletePlayConfirm,
	deleteConceptConfirm,
	onOpenPlay,
	renamePlayId,
	renamePlayName,
}: UsePlaybookActionsParams): UsePlaybookActionsReturn {
	const navigate = useNavigate()
	// =========================================================================
	// Play Actions
	// =========================================================================

	const handleNewPlay = useCallback(async () => {
		try {
			const newPlay = await playData.createPlay('Untitled Play')

			stateActions.resetNewItemState()

			if (onOpenPlay) {
				onOpenPlay(newPlay.id)
			}
		} catch (err) {
			console.error('Failed to create play:', err)
			alert(err instanceof Error ? err.message : 'Failed to create play')
		}
	}, [playData, stateActions, onOpenPlay])

	const handleOpenPlay = useCallback((playId: string) => {
		onOpenPlay?.(playId)
	}, [onOpenPlay])

	const handleAnimatePlay = useCallback((playId: string) => {
		stateActions.setViewingPlayId(playId)
		modals.open('playViewer')
	}, [stateActions, modals])

	const handleRenamePlay = useCallback((playId: string) => {
		const play = plays.find((p) => p.id === playId)
		if (!play) return
		stateActions.setRenamePlayId(playId)
		stateActions.setRenamePlayName(play.name)
		modals.open('rename')
	}, [plays, stateActions, modals])

	const confirmRename = useCallback(async () => {
		if (!renamePlayId || !renamePlayName.trim()) return

		try {
			await playData.updatePlay(renamePlayId, { name: renamePlayName })
			modals.close('rename')
			stateActions.resetRenameState()
		} catch (err) {
			console.error('Failed to rename play:', err)
			alert(err instanceof Error ? err.message : 'Failed to rename play')
		}
	}, [renamePlayId, renamePlayName, playData, modals, stateActions])

	const handleDeletePlay = useCallback((playId: string) => {
		deletePlayConfirm.prompt(playId)
	}, [deletePlayConfirm])

	const handleDuplicatePlay = useCallback(async (playId: string) => {
		try {
			await playData.duplicatePlay(playId)
		} catch (err) {
			console.error('Failed to duplicate play:', err)
			alert(err instanceof Error ? err.message : 'Failed to duplicate play')
		}
	}, [playData])

	// =========================================================================
	// Concept/Formation Actions
	// =========================================================================

	const handleDeleteConcept = useCallback((id: string, type: ConceptType) => {
		deleteConceptConfirm.prompt({ id, type })
	}, [deleteConceptConfirm])

	const handleNewFormation = useCallback(() => {
		navigate(`/playbooks/${playbookId}/formation/new`)
	}, [navigate, playbookId])

	const handleEditFormation = useCallback((formation: Formation) => {
		navigate(`/playbooks/${playbookId}/formation/${formation.id}`)
	}, [navigate, playbookId])

	return {
		allPlays: plays,
		handleNewPlay,
		handleOpenPlay,
		handleAnimatePlay,
		handleRenamePlay,
		confirmRename,
		handleDeletePlay,
		handleDuplicatePlay,
		handleDeleteConcept,
		handleNewFormation,
		handleEditFormation,
	}
}
